import React, { useEffect, useRef, useState } from 'react';
import TokenService from '../utils/TokenService';
import { InputText } from 'primereact/inputtext';
import { Mixpanel } from '../utils/Mixpanel';
import { useMutation } from 'react-query';
import idApi from '../service/IdApi';
import { throttle } from 'lodash';
import { useAppContext } from '../context/appContext';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import DataUtils from '../utils/DataUtils';

const AccountInfoNeeded = ({ showAccountInfoNeededDialog, hideAccountInfoNeededDialog, accountInfoNeededReason }) => {
    const { displayAlert } = useAppContext();
    const [isVisible, setIsVisible] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [firstName, setFirstName] = useState(TokenService.getFirstName());
    const [lastName, setLastName] = useState(TokenService.getLastName());
    const [changesMade, setChangesMade] = useState(false);
    const firstNameRef = useRef(null);
    const [step, setStep] = useState(1);

    useEffect(() => {
        if (showAccountInfoNeededDialog) {
            setStep(1);
            setIsVisible(true);
        }
        else {
            setIsVisible(false);
        }
    }, [showAccountInfoNeededDialog]);

    const handleFirstNameChange = (e) => {
        setFirstName(e.target.value);
        setChangesMade(true);
    };

    const handleLastNameChange = (e) => {
        setLastName(e.target.value);
        setChangesMade(true);
    };

    const updateAccount = useMutation(() => {
        var body = {};
        body.firstName = firstName.trim();
        body.lastName = lastName.trim();
        try {
            const data = idApi.patch("auth/user", body).then((response) => response.data);;
            return data;
        } catch (error) {
            throw error;
        }
    });

    const onSubmit = (e) => {
        e.preventDefault();
        submitAction();
    };

    const save = (e) => {
        e.preventDefault();
        submitAction();
    };

    const submitAction = throttle(() => {
        setIsProcessing(true);
        if (DataUtils.isEmpty(firstName)) {
            displayAlert("error", "Error", "A first name is required");
            setIsProcessing(false);
            return;
        }
        if (firstName.trim().length === 1) {
            displayAlert("error", "Error", "First name must be at least 2 characters");
            setIsProcessing(false);
            return;
        }
        if (DataUtils.isEmpty(lastName)) {
            displayAlert("error", "Error", "A last name is required");
            setIsProcessing(false);
            return;
        }
        if (lastName.trim().length === 1) {
            displayAlert("error", "Error", "Please use your full last name");
            setIsProcessing(false);
            return;
        }
        updateAccount.mutate({}, {
            onError: (error) => {
                console.error('Failed to update account info:', error);
                var mixpanelMessage = "Error updating account info: " + error;
                Mixpanel.track("UI ID - Javascript error", {
                    'Javascript Error': mixpanelMessage
                });
                displayAlert("error", "Unexpected Error", "Unexpected error. Please try again later");
                setIsProcessing(false);
            },
            onSuccess: (data) => {
                setChangesMade(false);
                displayAlert("success", "Success", "Account information updated");
                setIsProcessing(false);
                TokenService.setAccessToken(data.userAccessToken);
                TokenService.setRefreshToken(data.userRefreshToken);
                setStep(2);
            },
        }
        );
    }, 500, { trailing: false });

    const onAccountInfoNeededDialogShow = () => {
        setTimeout(() => {
            if (firstNameRef.current) {
                firstNameRef.current.focus();
            }
        }, 50);
    };

    return (
        <div>
            <Dialog
                visible={isVisible}
                style={{ width: "395px" }}
                header="Account Information Required"
                modal
                footer={(
                    <>
                        {step === 1 && (
                            <div>
                                <Button label="Cancel" icon="pi pi-times" className="p-button-text p-button-danger" onClick={hideAccountInfoNeededDialog} />
                                <Button label="Save" icon="pi pi-check" className="ml-2" disabled={!changesMade} onClick={save} loading={isProcessing} />
                            </div>
                        )}
                        {step === 2 && (
                            <div>
                                <Button label="Close" icon="pi pi-times" onClick={hideAccountInfoNeededDialog} />
                            </div>
                        )}
                    </>
                )}
                onHide={hideAccountInfoNeededDialog}
                onShow={onAccountInfoNeededDialogShow}
            >
                <div className="flex">
                    {step === 1 && (
                        <form className="form" onSubmit={onSubmit}>
                            <div className="text-left text-lg mb-2">Before you can {accountInfoNeededReason}, you must complete your account information.</div>
                            <div className="text-left text-lg mb-2 font-medium">Your Name</div>
                            <div className="p-fluid formgrid grid">
                                <div className="field col-12">
                                    <label htmlFor="firstname" className="required">First Name</label>
                                    <InputText
                                        id="firstname"
                                        type="text"
                                        onChange={handleFirstNameChange}
                                        autoFocus
                                        value={firstName || ""}
                                        ref={firstNameRef}
                                    />
                                </div>
                                <div className="field col-12">
                                    <label htmlFor="lastname" className="required">Last Name</label>
                                    <InputText
                                        id="lastname"
                                        type="text"
                                        onChange={handleLastNameChange}
                                        value={lastName || ""}
                                    />
                                </div>
                            </div>
                            <input type="submit" style={{ display: "none" }} />
                        </form>
                    )}
                    {step === 2 && (
                        <div>
                            <div className="flex flex-column">
                                <div className="flex flex-row text-green-400 text-lg">
                                    <div className="mr-2">
                                        <i className="pi pi-check-circle" style={{ fontSize: '1.2rem' }}></i>
                                    </div>
                                    <div className="text-green-400">
                                        Your account has been updated.
                                    </div>
                                </div>
                                <div className="mt-2">
                                    You can now {accountInfoNeededReason}.
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Dialog>
        </div>
    );
};

export default AccountInfoNeeded;
